@if (!isLoading()) {
  <div class="invoice-wrapper">
    <!-- Header -->
    <div class="invoice-header">
      <h2 class="primary-font-semibold">
        {{ 'invoice_details.invoice' | translate }} {{ orderDetails()?.orderId }}
      </h2>
      <button
        class="download-btn primary-font-medium secondary-button rounded-pill custom-button"
        (click)="downloadInvoice().subscribe()"
      >
        @if (!isSubmitting()) {
          {{ 'my_esims.esim_plan_detail.download_invoice' | translate }}
          <span class="download-icon">
            <svg
              class="rtl"
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M3.33203 5.83464C3.82356 6.34034 5.13181 8.33464 5.83203 8.33464C6.53226 8.33464 7.8405 6.34034 8.33203 5.83464M5.83203 7.5013V1.66797"
                stroke="black"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M3.33203 10.8346V12.1215C3.33203 14.8256 3.33203 16.1777 4.07042 17.0936C4.2196 17.2786 4.38812 17.4471 4.57314 17.5962C5.48896 18.3346 6.84105 18.3346 9.5452 18.3346C10.1332 18.3346 10.4271 18.3346 10.6964 18.2396C10.7524 18.2199 10.8072 18.1971 10.8608 18.1716C11.1184 18.0483 11.3262 17.8405 11.7419 17.4247L15.689 13.4776C16.1708 12.9959 16.4116 12.7551 16.5385 12.4487C16.6654 12.1425 16.6654 11.8018 16.6654 11.1206V8.33464C16.6654 5.19194 16.6654 3.62059 15.689 2.64428C14.7128 1.66797 13.1414 1.66797 9.9987 1.66797M10.832 17.918V17.5013C10.832 15.1443 10.832 13.9658 11.5643 13.2336C12.2965 12.5013 13.475 12.5013 15.832 12.5013H16.2487"
                stroke="black"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </span>
        } @else {
          <output class="spinner-border spinner-border-sm" aria-hidden="true"></output>
        }
      </button>
    </div>

    <!-- Address Section -->
    <div class="address-grid">
      <div>
        <h4 class="primary-font-semibold">
          {{ 'invoice_details.customer_details' | translate }}
        </h4>
        <div class="customer-details">
          <p class="primary-font-medium">
            {{ customerDetails()?.firstName }} {{ customerDetails()?.lastName }}
          </p>
          <p class="primary-font-medium">{{ customerDetails()?.email }}</p>
          <p class="primary-font-medium">{{ customerDetails()?.mobile }}</p>
        </div>
      </div>

      <div>
        <h4 class="primary-font-semibold">{{ 'invoice_details.bill_from' | translate }}</h4>
        <p class="primary-font-medium">{{ billFrom()?.name }}</p>
        <p class="primary-font-medium">{{ billFrom()?.addressLine }}</p>
        <p class="primary-font-medium">
          <span>{{ billFrom()?.landmark }}, </span>
          <span>{{ billFrom()?.city }}, </span>
          <span>{{ billFrom()?.state }}, </span>
          <span>{{ billFrom()?.country }}, </span>
          <span>- {{ billFrom()?.pincode }} </span>
        </p>
      </div>

      <div>
        <h4 class="primary-font-semibold">{{ 'invoice_details.billing_contact' | translate }}</h4>
        <a href="mailto:{{ billingContact()?.emailAddress }}" class="link primary-font-medium">{{
          billingContact()?.emailAddress
        }}</a>
        <p class="primary-font-medium">
          {{ billingContact()?.countryCode }} {{ billingContact()?.phoneNumber }}
        </p>
      </div>
    </div>

    <hr class="separator" />

    <!-- Summary -->
    <h3 class="section-title primary-font-bold">{{ 'invoice_details.summary' | translate }}</h3>

    <div class="summary-table">
      <div class="summary-table-content">
        <div class="table-header primary-font-semibold">
          <span>{{ 'invoice_details.qty' | translate }}</span>
          <span>{{ 'invoice_details.product' | translate }}</span>
          <span>{{ 'invoice_details.unit_price' | translate }}</span>
          <span>{{ 'invoice_details.amount' | translate }}</span>
        </div>

        <div class="table-row primary-font-medium">
          <span>1</span>
          <span>{{ translatedPlanName() || (orderDetails()?.name | titlecase) }}</span>
          <span>{{ orderDetails()?.currency || '' }} {{ orderDetails()?.planPrice | number: '1.2-2' }}</span>
          <span>{{ orderDetails()?.currency || '' }} {{ orderDetails()?.planPrice | number: '1.2-2' }}</span>
        </div>
      </div>

      <div class="price-breakdown">
        <div class="primary-font-bold">
          <span>{{ 'invoice_details.total_price' | translate }}:</span>
          <span>{{ orderDetails()?.currency || '' }} {{ orderDetails()?.planPrice | number: '1.2-2' }}</span>
        </div>
        @if (orderDetails()?.couponCode) {
          <div>
            <span class="primary-font-bold"
              >{{ 'invoice_details.coupon_code' | translate }}
              <span class="deductions">{{ orderDetails()?.couponCode }}</span
              >:</span
            >
            <span class="primary-font-semibold deductions"
              >- {{ orderDetails()?.currency || ''
              }} {{ orderDetails()?.couponAmount | number: '1.2-2' }}</span
            >
          </div>
        }
        @if (orderDetails()?.rewardPointsRedeemed) {
          <div>
            <span class="primary-font-bold"
              >{{ 'invoice_details.reward_points' | translate }}
              <span class="deductions">{{ orderDetails()?.rewardPointsRedeemed }}</span
              >:</span
            >
            <span class="primary-font-semibold deductions"
              >- {{ orderDetails()?.currency || ''
              }} {{ orderDetails()?.rewardPointRedeemedCashValue | number: '1.2-2' }}</span
            >
          </div>
        }
        <div class="final">
          <span class="primary-font-bold">{{ 'invoice_details.final_price' | translate }}:</span>
          <span class="primary-font-bold"
            >{{ orderDetails()?.currency || ''
            }} {{ orderDetails()?.paidAmount | number: '1.2-2' }}</span
          >
        </div>
      </div>
    </div>

    <!-- Transaction -->
    <h3 class="section-title primary-font-bold">{{ 'invoice_details.transaction' | translate }}</h3>

    <div class="transaction-table" [class.refunded-table]="plan()?.status === 'refunded'">
      <div class="table-header primary-font-bold">
        <span>{{ 'invoice_details.date' | translate }}</span>
        <span>{{ 'invoice_details.transaction_id' | translate }}</span>
        <span>{{ 'invoice_details.amount' | translate }}</span>
        <span>{{ 'invoice_details.method' | translate }}</span>
        <span>{{ 'invoice_details.status' | translate }}</span>
        @if (plan()?.status === 'refunded') {
          <span>{{ 'invoice_details.refund_amount' | translate }}</span>
        }
      </div>

      <div class="table-row primary-font-medium">
        <span>{{ orderDetails()?.createdAt | date: 'dd-MM-yyyy' }}</span>
        <span>{{ plan()?.transactionId }}</span>
        <span>{{ orderDetails()?.currency || '' }} {{ orderDetails()?.paidAmount | number: '1.2-2' }}</span>
        <span> {{ orderDetails()?.paymentMethod }}</span>
        <span class="success">{{ 'common.success' | translate }}</span>

        @if (plan()?.status === 'refunded') {
          <span>
            {{ orderDetails()?.currency || ''
            }} {{ orderDetails()?.paidAmount | number: '1.2-2' }}</span
          >
        }
      </div>
    </div>
  </div>
}

@if (isLoading()) {
  <div class="invoice-wrapper border rounded-4 p-4 placeholder-glow">
    <!-- Header -->
    <div class="invoice-header d-flex justify-content-between align-items-center">
      <div class="col-4 col-lg-4 col-xxl-4">
        <p class="mb-0 placeholder w-100"><span></span></p>
      </div>

      <div class="col-md-4 col-lg-3 col-xxl-2">
        <button
          class="download-btn btn default-button disabled rounded-pill placeholder custom-button w-100"
        >
          <span class="placeholder w-100"></span>
        </button>
      </div>
    </div>

    <!-- Address Section -->
    <div class="address-grid mt-4">
      <div>
        <p>
          <span class="placeholder col-6"></span>
        </p>

        <div class="customer-details">
          <p><span class="placeholder col-8"></span></p>
          <p><span class="placeholder col-9"></span></p>
          <p><span class="placeholder col-6"></span></p>
        </div>
      </div>

      <div>
        <p>
          <span class="placeholder col-6"></span>
        </p>

        <p><span class="placeholder col-10"></span></p>
        <p><span class="placeholder col-9"></span></p>
        <p><span class="placeholder col-11"></span></p>
      </div>

      <div>
        <p>
          <span class="placeholder col-7"></span>
        </p>

        <p><span class="placeholder col-9"></span></p>
        <p><span class="placeholder col-6"></span></p>
      </div>
    </div>

    <hr class="separator" />

    <!-- Summary -->
    <p class="section-title">
      <span class="placeholder col-3"></span>
    </p>

    <div class="summary-table">
      <div class="table-header">
        <span><span class="placeholder col-6"></span></span>
        <span><span class="placeholder col-10"></span></span>
        <span><span class="placeholder col-6"></span></span>
        <span><span class="placeholder col-6"></span></span>
      </div>

      <div class="table-row">
        <span><span class="placeholder col-4"></span></span>
        <span><span class="placeholder col-11"></span></span>
        <span><span class="placeholder col-6"></span></span>
        <span><span class="placeholder col-6"></span></span>
      </div>

      <div class="price-breakdown">
        <div>
          <span class="placeholder col-5"></span>
          <span class="placeholder col-2"></span>
        </div>
        <div>
          <span class="placeholder col-6"></span>
          <span class="placeholder col-2"></span>
        </div>
        <div>
          <span class="placeholder col-6"></span>
          <span class="placeholder col-2"></span>
        </div>
        <div>
          <span class="placeholder col-5"></span>
          <span class="placeholder col-2"></span>
        </div>
      </div>
    </div>

    <!-- Transaction -->
    <p class="section-title">
      <span class="placeholder col-4"></span>
    </p>

    <div class="transaction-table">
      <div class="table-header">
        <span><span class="placeholder col-8"></span></span>
        <span><span class="placeholder col-10"></span></span>
        <span><span class="placeholder col-6"></span></span>
        <span><span class="placeholder col-9"></span></span>
        <span><span class="placeholder col-6"></span></span>
      </div>

      <div class="table-row">
        <span><span class="placeholder col-8"></span></span>
        <span><span class="placeholder col-11"></span></span>
        <span><span class="placeholder col-6"></span></span>
        <span><span class="placeholder col-10"></span></span>
        <span><span class="placeholder col-6"></span></span>
      </div>
    </div>
  </div>
}
